// MapUtils.ts
import { map, latLng, tileLayer, Marker, Icon, LayerGroup, Circle, MapOptions, type Map } from "leaflet";
import $ from "jquery";
import { t } from "~/hooks/useI18n";

let mapInitialized: any | undefined;// su eventi di history back/forward, se la pagina è stata caricata dalla cache del browser

/**
 * Helper per generare la description del marker con prezzo formattato
 * @param lottoId ID del lotto
 * @param debug Se true, logga i valori in console (default: false)
 * @returns Stringa HTML formattata per il popup del marker
 */
function getMarkerDescription(lottoId: number, debug: boolean = false): string {
    const titolo = $("#Titolo").val() as string;
    const indirizzo = $("#Indirizzo").val() as string;
    const prezzo = $("#PrezzoRichiesto").val() as string;
    const prezzoVisibile = $("#PrezzoVisibile").val() as string;
    const trattativaRiservata = $("#TrattativaRiservata").val() as string;

    if (debug) {
        console.info("Prezzo --> " + prezzo);
        console.info("PrezzoVisibile --> " + prezzoVisibile);
        console.info("TrattativaRiservata --> " + trattativaRiservata);
    }

    let prezzoRichiesto = "-";
    if (trattativaRiservata == "true") {
        prezzoRichiesto = t("common.trattativaRiservata");
    } else if (prezzo && prezzoVisibile == "true") {
        prezzoRichiesto = parseFloat(prezzo.replace(',', '.')).toLocaleString('it-IT', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        });
    }

    return `<b>${titolo}</b><br/>${indirizzo}<br/><b>${prezzoRichiesto}</b><br/> Cod. ${lottoId}`;
}

/**
 * Inizializza la mappa per un lotto specifico.
 * @param lottoId
 * @constructor
 */
export async function InitMap(lottoId: number): Promise<void> {
    // Codice della funzione InitMap
    // Inizializza la mappa se l'elemento esiste
    const _lat = $("#Latitudine").val() as string;
    const _lng = $("#Longitudine").val() as string;
    if (!$("#map").length || !_lat || !_lng) {
        console.log("Mappa non presente");
        $("#sectionMappa").hide();
        return;
    }

    const lat = parseFloat(_lat.replace(',', "."));
    const lng = parseFloat(_lng.replace(',', "."));

    const mymap = (mapInitialized as Map) ?? map('map');
    mapInitialized = mymap;

    const osmUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const osmAttrib = 'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors';
    const osm = tileLayer(osmUrl, { minZoom: 11, maxZoom: 20, attribution: osmAttrib });
    mymap.setView(latLng(lat, lng), 14);
    mymap.addLayer(osm);
    mymap.scrollWheelZoom.disable();
    mymap.dragging.disable();
    //Aggiungo il marker della vendita
    const marker = new Marker(latLng(lat, lng)).addTo(mymap);
    const description = getMarkerDescription(lottoId, true); // ✅ Con debug log
    marker.bindPopup(description).openPopup();

}

/**
 * Inizializza la mappa dei punti di interesse (POI) intorno a un lotto specifico.
 * @param lottoId
 * @constructor
 */
export async function InitMapPoi(lottoId: number): Promise<void> {
    // Codice della funzione InitMapPoi
    // Inizializza la mappa se l'elemento esiste
    const _lat = $("#Latitudine").val() as string;
    const _lng = $("#Longitudine").val() as string;

    if (!$("#mappoi").length || !_lat || !_lng) {
        console.log("Mappa non presente");
        $("#sectionMappaPoi").hide();
        return;
    }

    const lat = parseFloat(_lat.replace(',', "."));
    const lng = parseFloat(_lng.replace(',', "."));

    const options: MapOptions = {
        center: [lat, lng],
        zoom: 15,
        // zoomControl: false,
        // dragging: false,
        // scrollWheelZoom: false,
        // doubleClickZoom: false,
        // minZoom: 11,
        // maxZoom: 20,
        // touchZoom: false
    };

    const mymap = map('mappoi', options);
    const osmUrl = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
    const osmAttrib = 'Map data © <a href="https://openstreetmap.org">OpenStreetMap</a> contributors';
    const osm = tileLayer(osmUrl, { minZoom: 11, maxZoom: 20, attribution: osmAttrib });
    mymap.setView(latLng(lat, lng), 14);
    mymap.addLayer(osm);
    mymap.scrollWheelZoom.disable();
    mymap.dragging.disable();
    //Aggiungo il marker della vendita
    const marker = new Marker(latLng(lat, lng)).addTo(mymap);
    const description = getMarkerDescription(lottoId, true); // ✅ Con debug log
    marker.bindPopup(description).openPopup();


    const schoolIcon = new Icon({
        iconUrl: '../assets/images/icon/education.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const bankIcon = new Icon({
        iconUrl: '../assets/images/icon/bank.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const restaurantIcon = new Icon({
        iconUrl: '../assets/images/icon/restaurant.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const pharmacyIcon = new Icon({
        iconUrl: '../assets/images/icon/pharmacy.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const hospitalIcon = new Icon({
        iconUrl: '../assets/images/icon/hospital.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const cinemaIcon = new Icon({
        iconUrl: '../assets/images/icon/cinema.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const busIcon = new Icon({
        iconUrl: '../assets/images/icon/bus.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const chargeIcon = new Icon({
        iconUrl: '../assets/images/icon/charge.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });
    const parkingIcon = new Icon({
        iconUrl: '../assets/images/icon/parking.svg',
        iconSize: [32, 32], // Dimensione dell'icona
        iconAnchor: [16, 32], // Punto di ancoraggio dell'icona
        popupAnchor: [0, -32] // Punto da dove compare il popup
    });

    const amenityCount: Record<string, number> = {}; // Dichiarazione corretta di amenityCount
    const aroundPOIMeters = 600;// Distanza in metri per cercare i POI intorno al marker
    let amenityTranslations: Record<string, string> = {
        'school': 'Scuola',
        'hospital': 'Ospedale',
        'cinema': 'Cinema',
        'restaurant': 'Ristorante',
        'pharmacy': 'Farmacia',
        'parking': 'Parcheggio',
        'university': 'Università',
        'park': 'Parco',
        'bank': 'Banca',
        'supermarket': 'Supermercato',
        'bus_station': 'Stazione bus',
        'charging_station': 'Stazione ricarica elettrica',
    };

    const schoolLayer = new LayerGroup();
    const bankLayer = new LayerGroup();
    const hospitalLayer = new LayerGroup();
    const cinemaLayer = new LayerGroup();
    const restaurantLayer = new LayerGroup();
    const pharmacyLayer = new LayerGroup();
    const busStationLayer = new LayerGroup();
    const chargingStationLayer = new LayerGroup();
    const parkingLayer = new LayerGroup();
    const universityLayer = new LayerGroup();

    const layers: Record<string, LayerGroup> = {
        'cinema': cinemaLayer,
        'school': schoolLayer,
        'bank': bankLayer,
        'hospital': hospitalLayer,
        'restaurant': restaurantLayer,
        'pharmacy': pharmacyLayer,
        'bus_station': busStationLayer,
        'charging_station': chargingStationLayer,
        'parking': parkingLayer,
        'university': universityLayer
    };
    if (Object.entries(amenityCount).length > 0) {
        new Circle([lat, lng], {
            color: 'blue',
            fillColor: '#1C93FF',
            fillOpacity: 0.3,
            radius: aroundPOIMeters
        }).addTo(mymap);
    }

    // Mirror pubblici dell'Overpass API, provati in ordine finché uno risponde correttamente
    const OVERPASS_MIRRORS = [
        "https://overpass-api.de/api/interpreter",
        "https://overpass.kumi.systems/api/interpreter",
        "https://overpass.openstreetmap.ru/api/interpreter",
        "https://overpass.private.coffee/api/interpreter",
    ];
    const OVERPASS_TIMEOUT_MS = 10000; // 10s per tentativo, più tollerante verso i server pubblici

    /**
     * Esegue la query Overpass provando in sequenza i mirror disponibili.
     * Passa al mirror successivo in caso di timeout, errore di rete o risposta non valida.
     */
    async function fetchOverpassData(query: string): Promise<any> {
        let lastError: any;

        for (const baseUrl of OVERPASS_MIRRORS) {
            const url = baseUrl + "?data=" + encodeURIComponent(query);

            console.log("🗺️ Chiamata Overpass API:", baseUrl);

            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), OVERPASS_TIMEOUT_MS);

            try {
                const response = await fetch(url, { signal: controller.signal });
                clearTimeout(timeoutId);

                console.log("✅ Fetch completato, status:", response.status, "-", baseUrl);

                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const contentType = response.headers.get("content-type");
                if (!contentType || !contentType.includes("application/json")) {
                    throw new Error("Risposta non è JSON valido");
                }

                return await response.json();
            } catch (error: any) {
                clearTimeout(timeoutId);
                lastError = error;
                console.warn(`⚠️ Mirror Overpass non disponibile (${baseUrl}):`, error.message ?? error);
                // Prova il mirror successivo
            }
        }

        // Tutti i mirror hanno fallito
        throw lastError ?? new Error("Nessun mirror Overpass disponibile");
    }

    async function getPOI(lat: number, lng: number) {
        const query = `
        [out:json];
        (
            node["amenity"="school"](around:600, ${lat}, ${lng});
            node["amenity"="university"](around:600, ${lat}, ${lng});
            node["amenity"="bank"](around:600, ${lat}, ${lng});
            node["amenity"="hospital"](around:600, ${lat}, ${lng});
            node["amenity"="cinema"](around:600, ${lat}, ${lng});
            node["amenity"="restaurant"](around:600, ${lat}, ${lng});
            node["amenity"="pharmacy"](around:600, ${lat}, ${lng});
            node["amenity"="bus_station"](around:600, ${lat}, ${lng});
            node["amenity"="charging_station"](around:600, ${lat}, ${lng});
            node["amenity"="parking"](around:600, ${lat}, ${lng});
        );
        out body;
    `;

        try {
            const data = await fetchOverpassData(query);

            data.elements.forEach(function (poi: any) {
                var icon, layer;
                switch (poi.tags.amenity) {
                    case 'school':
                    case 'university':
                        icon = schoolIcon;
                        layer = schoolLayer;
                        break;
                    case 'bank':
                        icon = bankIcon;
                        layer = bankLayer;
                        break;
                    case 'hospital':
                        icon = hospitalIcon;
                        layer = hospitalLayer;
                        break;
                    case 'cinema':
                        icon = cinemaIcon;
                        layer = cinemaLayer;
                        break;
                    case 'restaurant':
                        icon = restaurantIcon;
                        layer = restaurantLayer;
                        break;
                    case 'pharmacy':
                        icon = pharmacyIcon;
                        layer = pharmacyLayer;
                        break;
                    case 'bus_station':
                        icon = busIcon;
                        layer = busStationLayer;
                        break;
                    case 'charging_station':
                        icon = chargeIcon;
                        layer = chargingStationLayer;
                        break;
                    case 'parking':
                        icon = parkingIcon;
                        layer = parkingLayer;
                        break;
                    default:
                        layer = null;
                        break;
                }
                if (icon && layer) {
                    new Marker([poi.lat, poi.lon], { icon: icon })
                        .bindPopup(`<b>${poi.tags.name || 'no nome'}</b><br>Tipo: ${amenityTranslations[poi.tags.amenity]}`)
                        .addTo(layer);
                    if (typeof poi.tags.amenity === "string") {
                        if (amenityCount[poi.tags.amenity]) {
                            amenityCount[poi.tags.amenity]++;
                        } else {
                            amenityCount[poi.tags.amenity] = 1;
                        }
                    }
                } else {
                    alert(poi.tags.amenity);
                }
            });

            schoolLayer.addTo(mymap);
            bankLayer.addTo(mymap);
            hospitalLayer.addTo(mymap);
            cinemaLayer.addTo(mymap);
            restaurantLayer.addTo(mymap);
            pharmacyLayer.addTo(mymap);
            busStationLayer.addTo(mymap);
            chargingStationLayer.addTo(mymap);
            parkingLayer.addTo(mymap);
            generaReport();

        } catch (error: any) {
            // ✅ Gestione specifica per timeout
            if (error.name === 'AbortError') {
                console.warn("⏱️ Timeout su tutti i mirror Overpass API");
                console.warn("   La mappa sarà visualizzata senza punti di interesse");
            } else if (error.message?.includes("status: 504")) {
                console.error("❌ Gateway Timeout (504) da Overpass API");
                console.error("   Il server Overpass non ha risposto in tempo");
            } else {
                console.error("❌ Errore caricamento POI:", error);
                console.error("   Tipo errore:", error.message);
            }

            // ✅ Mostra comunque la mappa senza POI (amenityCount rimane vuoto)
            // generaReport() gestisce il caso senza POI nascondendo solo il report
            generaReport();
        }
    }

    function generaReport() {
        if (Object.entries(amenityCount).length === 0) {
            $(".nopoi").hide();
            $("#mappaPoiSubtitle").hide();
            const defaultTitle = $("#mappaPoiTitle").data("title-default");
            if (defaultTitle) {
                $("#mappaPoiTitle").text(defaultTitle);
            }

            $("#actionPoi").addClass("d-none");

            const marker = new Marker(latLng(lat, lng)).addTo(mymap);
            const IdLotto = $("#LottoId").val() as string;
            const description = getMarkerDescription(parseInt(IdLotto), true); // ✅ Con debug log
            marker.bindPopup(description).openPopup();
            mymap.setZoom(14);
            return;
        } else {
            const poiTitle = $("#mappaPoiTitle").data("title-poi");
            if (poiTitle) {
                $("#mappaPoiTitle").text(poiTitle);
            }
            $("#mappaPoiSubtitle").show();
            $("#actionPoi").removeClass("d-none");

        }

        const reportDiv = document.getElementById('reportpoi') as HTMLDivElement;
        reportDiv.innerHTML = '';
        for (var amenity in amenityCount) {
            var iconUrl;
            if (amenity === 'school' || amenity === 'university') {
                iconUrl = schoolIcon.options.iconUrl;
            }
            if (amenity === 'bank') {
                iconUrl = bankIcon.options.iconUrl;
            }
            if (amenity === 'hospital') {
                iconUrl = hospitalIcon.options.iconUrl;
            }
            if (amenity === 'restaurant') {
                iconUrl = restaurantIcon.options.iconUrl;
            }
            if (amenity === 'pharmacy') {
                iconUrl = pharmacyIcon.options.iconUrl;
            }
            if (amenity === 'bus_station') {
                iconUrl = busIcon.options.iconUrl;
            }
            if (amenity === 'cinema') {
                iconUrl = cinemaIcon.options.iconUrl;
            }
            if (amenity === 'charging_station') {
                iconUrl = chargeIcon.options.iconUrl;
            }
            if (amenity === 'parking') {
                iconUrl = parkingIcon.options.iconUrl;
            }

            if (iconUrl) {
                reportDiv.innerHTML += `
                            <div class="poitoggle" id="${amenity}" class="cursor-pointer">
                              <div class="p-2 border mb-1 rounded">
                                   <div class="d-flex align-items-center justify-content-between">
                                   <div class="d-flex align-items-center">
                                      <img src="${iconUrl}" style="width: 32px; height: 32px; vertical-align: middle;" alt="icona ${amenityTranslations[amenity]}"/>
                                      <p class="p-0 m-0 fw-bold small mx-2">${amenityTranslations[amenity]}</p>
                                    </div>
                                      <p class="p-0 m-0 h5 fw-bold mx-2">${amenityCount[amenity]}</p>
                                   </div>
                                </div>
                            </div>
                          `;
            }
        }
        var poiToggles = document.querySelectorAll('.poitoggle') as NodeListOf<HTMLElement>;
        poiToggles.forEach((toggle: HTMLElement) => {
            toggle.addEventListener('click', () => {
                const amenity = toggle.id; // Usa `toggle` invece di `this`
                const layer = layers[amenity] as LayerGroup; // Tipizza correttamente `layers`
                if (layer) {
                    const isVisible = mymap.hasLayer(layer);
                    toggleLayer(layer, !isVisible);
                    if (!isVisible) {
                        toggle.classList.remove('deselected');
                    } else {
                        toggle.classList.add('deselected');
                    }
                }
            });
        });
    }

    function toggleLayer(layer: LayerGroup, isVisible: boolean): void {
        if (isVisible) {
            mymap.addLayer(layer);
        } else {
            mymap.removeLayer(layer);
        }
    }

    // ✅ Avvia caricamento POI con gestione errori
    try {
        await getPOI(lat, lng);
    } catch (error) {
        console.error("❌ Errore inaspettato durante l'inizializzazione POI:", error);
        // Mostra comunque la mappa senza POI
        generaReport();
    }
}